import React from 'react';
import { Box, Typography, useTheme, SxProps } from '@mui/material';
import { motion } from 'framer-motion';

interface LogoProps {
  variant?: 'full' | 'icon' | 'horizontal' | 'text';
  size?: 'small' | 'medium' | 'large';
  animated?: boolean;
  sx?: SxProps;
  onClick?: () => void;
}

const Logo: React.FC<LogoProps> = ({ 
  variant = 'full', 
  size = 'medium', 
  animated = false,
  sx,
  onClick 
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  const sizes = {
    small: { icon: 32, full: 120, horizontal: 180 },
    medium: { icon: 48, full: 160, horizontal: 240 },
    large: { icon: 64, full: 200, horizontal: 320 },
  };

  const currentSize = sizes[size];

  // SVG Logo Component
  const LogoSVG = () => {
    const scale = currentSize.full / 200; // Base size is 200

    if (variant === 'icon') {
      return (
        <svg 
          width={currentSize.icon} 
          height={currentSize.icon} 
          viewBox="0 0 48 48" 
          fill="none"
        >
          <g id="logo-icon">
            <path 
              d="M24 8 C24 5, 27 4, 28.5 5.5 C30 7, 28.5 10, 25.5 10 L24 10" 
              stroke={isDark ? '#FFFFFF' : '#1A1A1A'}
              strokeWidth="2" 
              fill="none"
              strokeLinecap="round"
            />
            <path 
              d="M10 18 L17 32 L24 18 L31 32 L38 18" 
              stroke={isDark ? '#FFFFFF' : '#1A1A1A'}
              strokeWidth="3" 
              fill="none"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <line 
              x1="24" y1="10" x2="24" y2="18" 
              stroke={isDark ? '#FFFFFF' : '#1A1A1A'}
              strokeWidth="2"
              strokeLinecap="round"
            />
            <circle cx="17" cy="32" r="2.5" fill="#D4AF37"/>
            <circle cx="31" cy="32" r="2.5" fill="#D4AF37"/>
          </g>
        </svg>
      );
    }

    if (variant === 'horizontal') {
      return (
        <svg 
          width={currentSize.horizontal} 
          height={currentSize.horizontal * 0.285} 
          viewBox="0 0 280 80" 
          fill="none"
        >
          <g id="logo-horizontal">
            <g transform="translate(20, 15)">
              <path 
                d="M20 10 C20 7, 23 6, 24.5 7.5 C26 9, 24.5 12, 21.5 12 L20 12" 
                stroke={isDark ? '#FFFFFF' : '#1A1A1A'}
                strokeWidth="2" 
                fill="none"
                strokeLinecap="round"
              />
              <path 
                d="M5 22 L12 36 L20 22 L28 36 L35 22" 
                stroke={isDark ? '#FFFFFF' : '#1A1A1A'}
                strokeWidth="3" 
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <line 
                x1="20" y1="12" x2="20" y2="22" 
                stroke={isDark ? '#FFFFFF' : '#1A1A1A'}
                strokeWidth="2"
                strokeLinecap="round"
              />
              <circle cx="12" cy="36" r="2.5" fill="#D4AF37"/>
              <circle cx="28" cy="36" r="2.5" fill="#D4AF37"/>
            </g>
            <text 
              x="75" y="42" 
              fontFamily="Montserrat, Arial, sans-serif" 
              fontSize="28" 
              fontWeight="700" 
              letterSpacing="2"
              fill={isDark ? '#FFFFFF' : '#1A1A1A'}
            >
              WARDROBE
            </text>
            <text 
              x="75" y="58" 
              fontFamily="Georgia, serif" 
              fontSize="12" 
              fontStyle="italic"
              fill={isDark ? '#CCCCCC' : '#666666'}
              letterSpacing="1"
            >
              Smart Style System
            </text>
            <line 
              x1="75" y1="65" x2="240" y2="65" 
              stroke="#D4AF37" 
              strokeWidth="1"
              opacity="0.5"
            />
          </g>
        </svg>
      );
    }

    // Full logo (default)
    return (
      <svg 
        width={currentSize.full} 
        height={currentSize.full} 
        viewBox="0 0 200 200" 
        fill="none"
      >
        <g id="logo-full" transform={`scale(${scale})`}>
          <path 
            d="M100 40 C100 30, 110 25, 115 30 C120 35, 115 45, 105 45 L100 45" 
            stroke={isDark ? '#FFFFFF' : '#1A1A1A'}
            strokeWidth="3" 
            fill="none"
            strokeLinecap="round"
          />
          <path 
            d="M50 70 L75 120 L100 70 L125 120 L150 70" 
            stroke={isDark ? '#FFFFFF' : '#1A1A1A'}
            strokeWidth="4" 
            fill="none"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <line 
            x1="100" y1="45" x2="100" y2="70" 
            stroke={isDark ? '#FFFFFF' : '#1A1A1A'}
            strokeWidth="3"
            strokeLinecap="round"
          />
          <circle cx="75" cy="120" r="4" fill="#D4AF37"/>
          <circle cx="125" cy="120" r="4" fill="#D4AF37"/>
          <line 
            x1="40" y1="140" x2="160" y2="140" 
            stroke={isDark ? '#FFFFFF' : '#1A1A1A'}
            strokeWidth="1"
            opacity="0.3"
          />
          <text 
            x="100" y="160" 
            fontFamily="Montserrat, Arial, sans-serif" 
            fontSize="18" 
            fontWeight="600" 
            letterSpacing="3"
            textAnchor="middle" 
            fill={isDark ? '#FFFFFF' : '#1A1A1A'}
          >
            WARDROBE
          </text>
          <text 
            x="100" y="175" 
            fontFamily="Georgia, serif" 
            fontSize="10" 
            fontStyle="italic"
            textAnchor="middle" 
            fill={isDark ? '#CCCCCC' : '#666666'}
          >
            Smart Style System
          </text>
        </g>
      </svg>
    );
  };

  // Text-only variant
  if (variant === 'text') {
    return (
      <Box 
        onClick={onClick}
        sx={{
          cursor: onClick ? 'pointer' : 'default',
          ...sx,
        }}
      >
        <Typography
          variant={size === 'small' ? 'h6' : size === 'large' ? 'h3' : 'h4'}
          sx={{
            fontFamily: '"Bebas Neue", "Oswald", sans-serif',
            fontWeight: 700,
            letterSpacing: '0.05em',
            color: theme.palette.text.primary,
          }}
        >
          WARDROBE
        </Typography>
        <Typography
          variant={size === 'small' ? 'caption' : 'body2'}
          sx={{
            fontFamily: '"Playfair Display", serif',
            fontStyle: 'italic',
            color: theme.palette.text.secondary,
            mt: -0.5,
          }}
        >
          Smart Style System
        </Typography>
      </Box>
    );
  }

  const logoElement = <LogoSVG />;

  if (animated) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        transition={{ duration: 0.3 }}
        onClick={onClick}
        style={{ cursor: onClick ? 'pointer' : 'default' }}
      >
        <Box sx={sx}>
          {logoElement}
        </Box>
      </motion.div>
    );
  }

  return (
    <Box 
      onClick={onClick}
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        ...sx,
      }}
    >
      {logoElement}
    </Box>
  );
};

export default Logo;